/**
 * SuggestedFocusAreaCard
 *
 * A single LLM-suggested focus area with Accept / Dismiss, plus the reasoning
 * behind it.
 *
 * The rationale matters more here than it looks. These suggestions make pointed
 * claims about the user's own behaviour — "building a case for leaving rather
 * than investing in repair" — and without the evidence behind them they read as
 * accusations rather than observations. The model has always produced a
 * rationale; it simply was not stored or shown.
 *
 * It is collapsed by default so the card stays scannable, and omitted entirely
 * when absent (manual areas, and rows predating the column) rather than
 * rendering an empty disclosure.
 *
 * Stateless; mutations are wired up by the parent.
 */

import { useState } from "react";
import type { FocusArea } from "@/types/focusArea";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, ChevronDown } from "lucide-react";

interface Props {
  focusArea: FocusArea;
  onAccept: (id: string) => void;
  onDismiss: (id: string) => void;
  isAccepting?: boolean;
  isDismissing?: boolean;
  /** Disables Accept when every slot is taken. */
  canAccept?: boolean;
}

export function SuggestedFocusAreaCard({
  focusArea,
  onAccept,
  onDismiss,
  isAccepting,
  isDismissing,
  canAccept = true,
}: Props) {
  const [showRationale, setShowRationale] = useState(false);
  const busy = isAccepting || isDismissing;

  return (
    <Card
      className="space-y-2 border-primary/30 bg-primary/5 p-4"
      data-testid={`suggested-focus-area-card-${focusArea.id}`}
    >
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-foreground">{focusArea.name}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {focusArea.description}
          </p>
        </div>
      </div>

      {focusArea.rationale && (
        <div className="pl-6">
          <button
            type="button"
            onClick={() => setShowRationale((open) => !open)}
            aria-expanded={showRationale}
            className="inline-flex items-center gap-1 rounded text-xs font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid={`suggestion-rationale-toggle-${focusArea.id}`}
          >
            <ChevronDown
              className={`h-3 w-3 transition-transform ${showRationale ? "rotate-180" : ""}`}
              aria-hidden="true"
            />
            Why this?
          </button>
          {showRationale && (
            <p
              className="mt-1.5 border-l-2 border-border pl-3 text-xs leading-relaxed text-muted-foreground"
              data-testid={`suggestion-rationale-${focusArea.id}`}
            >
              {focusArea.rationale}
            </p>
          )}
        </div>
      )}

      <div className="mt-2 flex items-center gap-2 pl-6">
        <Button
          size="sm"
          onClick={() => onAccept(focusArea.id)}
          disabled={busy || !canAccept}
          title={
            canAccept ? undefined : "Retire something first to free up a slot"
          }
        >
          Accept
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDismiss(focusArea.id)}
          disabled={busy}
        >
          Dismiss
        </Button>
      </div>
    </Card>
  );
}
